import { s__ } from '~/locale';

// Slash command sent to Duo Chat to trigger automatic failure analysis for the current CI job
// eslint-disable-next-line @gitlab/no-hardcoded-urls
export const DUO_CHAT_QUICK_ACTION_TROUBLESHOOT = '/troubleshoot';

export const i18n = {
  GITLAB_DUO: s__('AI|GitLab Duo'),
  GENIE_TOOLTIP: s__('AI|What does the selected code mean?'),
  GENIE_CHAT_LEGAL_GENERATED_BY_AI: s__('AI|Responses generated by AI'),
  REQUEST_ERROR: s__('AI|Something went wrong. Please try again later'),
  GENIE_CHAT_FEEDBACK_LINK: s__('AI|Give feedback to improve this answer.'),
  GENIE_CHAT_FEEDBACK_THANKS: s__('AI|Thank you for your feedback.'),
  AGENTIC_PROMPT_EXPLAIN_CODE: s__('AI|Explain this code.'),
  AGENTIC_PROMPT_SUMMARIZE_COMMENTS: s__('AI|Summarize the comments on this issue.'),
  AGENTIC_PROMPT_TROUBLESHOOT_PIPELINE: s__('AI|Troubleshoot this broken pipeline.'),
  AGENTIC_PROMPT_EXPLAIN_VULNERABILITY: s__(
    'AI|Explain this vulnerability and provide insights about potential solutions.',
  ),
};
export const AI_GENIE_DEBOUNCE = 300;
export const GENIE_CHAT_MODEL_ROLES = {
  user: 'user',
  system: 'system',
  assistant: 'assistant',
  tool: 'tool',
};

export const CHAT_MESSAGE_TYPES = {
  tool: 'tool',
  agent: 'agent',
  request: 'request',
};

export const EXPLAIN_CODE_TRACKING_EVENT_NAME = 'explain_code_blob_viewer';
export const TANUKI_BOT_TRACKING_EVENT_NAME = 'ask_gitlab_chat';
/* eslint-disable @gitlab/no-hardcoded-urls -- Duo Chat commands, not hardcoded URLs */
export const GENIE_CHAT_RESET_MESSAGE = '/reset';
export const GENIE_CHAT_CLEAR_MESSAGE = '/clear';
export const GENIE_CHAT_NEW_MESSAGE = '/new';
export const GENIE_CHAT_EXPLAIN_MESSAGE = '/explain';
/* eslint-enable @gitlab/no-hardcoded-urls */

export const COMMENT_TEMPERATURE_EVENTS = {
  MEASUREMENT_REQUESTED: 'requested_comment_temperature',
  HIGH_TEMP: 'detected_high_comment_temperature',
  REPEATED_HIGH_TEMP: 'detected_repeated_high_comment_temperature',
  FORCED_COMMENT: 'forced_high_temperature_commenting',
};

export const DUO_CHAT_VIEWS = {
  CHAT: 'chat',
  LIST: 'list',
};

export const DUO_WORKFLOW_CHAT_DEFINITION = 'chat';
export const DUO_WORKFLOW_NEW_CHAT_DEFINITION = 'agentic_chat/v1';
export const DUO_WORKFLOW_DEVELOPER_DEFINITION = 'developer/v1';
export const DUO_WORKFLOW_AGENT_PRIVILEGES = [2, 3, 7];
export const DUO_WORKFLOW_PRE_APPROVED_AGENT_PRIVILEGES = [2];
export const DUO_WORKFLOW_CLIENT_VERSION = '1.0';

// SSOT: https://docs.gitlab.com/api/graphql/reference/#duoworkflowstatus
export const DUO_WORKFLOW_STATUS_RUNNING = 'RUNNING';
export const DUO_WORKFLOW_STATUS_INPUT_REQUIRED = 'INPUT_REQUIRED';
export const DUO_WORKFLOW_STATUS_TOOL_CALL_APPROVAL_REQUIRED = 'TOOL_CALL_APPROVAL_REQUIRED';
export const DUO_WORKFLOW_STATUS_PLAN_APPROVAL_REQUIRED = 'PLAN_APPROVAL_REQUIRED';
export const DUO_WORKFLOW_STATUS_FINISHED = 'FINISHED';
export const DUO_WORKFLOW_STATUS_FAILED = 'FAILED';
export const DUO_WORKFLOW_STATUS_STOPPED = 'STOPPED';
// A client is still holding the websocket under these, so Workhorse still holds the
// lock. Named as the exception because an unrecognised status then frees the lock and
// risks one rejected send, where holding it would strand the queue with no release.
export const DUO_WORKFLOW_ATTACHED_STATUSES = [
  DUO_WORKFLOW_STATUS_RUNNING,
  DUO_WORKFLOW_STATUS_TOOL_CALL_APPROVAL_REQUIRED,
  DUO_WORKFLOW_STATUS_PLAN_APPROVAL_REQUIRED,
];
export const DUO_WORKFLOW_INACTIVE_CHAT_REASON = s__(
  'DuoAgenticChat|This Chat conversation has been archived and cannot receive new messages. Please start a new conversation to continue.',
);

export const DUO_WORKFLOW_ADDITIONAL_CONTEXT_REPOSITORY = 'repository';
export const DUO_WORKFLOW_ADDITIONAL_CONTEXT_USER_RULE = 'user_rule';
export const DUO_CURRENT_WORKFLOW_STORAGE_KEY = 'agenticChat.currentWorkflow';
// Prompts submitted during an active agent turn, fired one at a time as each turn
// completes. Per-tab so a queue cannot bleed into a chat running in another tab.
// Versioned: the record now names the workflow the prompts were typed against, and
// each vintage drops the other's shape rather than trying to read it.
export const DUO_CHAT_QUEUED_PROMPTS_STORAGE_KEY = 'agenticChat.queuedPrompts.v3';

// WebSocket URL and query parameters
export const DUO_WORKFLOW_WEBSOCKET_PARAM_ROOT_NAMESPACE_ID = 'root_namespace_id';
export const DUO_WORKFLOW_WEBSOCKET_PARAM_NAMESPACE_ID = 'namespace_id';
export const DUO_WORKFLOW_WEBSOCKET_PARAM_PROJECT_ID = 'project_id';
export const DUO_WORKFLOW_WEBSOCKET_PARAM_USER_SELECTED_MODEL = 'user_selected_model_identifier';
export const DUO_WORKFLOW_WEBSOCKET_PARAM_WORKFLOW_DEFINITION = 'workflow_definition';
export const DUO_WORKFLOW_WEBSOCKET_PARAM_AI_CATALOG_ITEM_VERSION_ID = 'ai_catalog_item_version_id';
export const DUO_WORKFLOW_WEBSOCKET_PARAM_WORKFLOW_ID = 'workflow_id';

// Client capabilities is how gitlab-lsp/browser -> workhorse -> Duo Workflow Service communicates
// capabilities that can be used by Duo Workflow Service without breaking
// backwards compatibility. We intersect the capabilities of all parties and
// then new behaviour can only depend on that behaviour if it makes it all the
// way through. Whenever you add to this list you must also update the constant in
// workhorse/internal/ai_assist/duoworkflow/runner.go and gitlab-lsp/browser before
// the feature becomes available.
export const DUO_AGENTIC_CHAT_CLIENT_CAPABILITIES = ['incremental_streaming', 'web_search'];

export const DUO_AGENTIC_CHAT_SELECTED_MODEL_KEY = 'agentic-chat-user-selected-model';
export const DUO_AGENTIC_CHAT_RECENT_MODELS_KEY = 'agentic-chat-recently-used-models';

export const DUO_AGENTIC_CHAT_PENDING_USER_MESSAGE_ID = 'pending-user-msg-id';

// eslint-disable-next-line @gitlab/no-hardcoded-urls -- Duo Chat command string, not a URL path
export const DUO_CHAT_QUICK_ACTION_SUMMARIZE = '/summarize_comments';

// Agent name identifier (not user-facing UI copy, do not translate)
export const DUO_CHAT_AGENT_GITLAB_DUO = 'GitLab Duo';
export const DUO_CHAT_AGENT_PLANNER = 'Planner';

export const AI_CONTEXT_ID_PAGE_CONTEXT = 'page-context';
export const AI_CONTEXT_ID_AGENT_MD = 'agents-md-user-instructions';
export const AI_CONTEXT_ID_CHAT_RULE = 'chat-rules-user-instructions';
export const AGENTS_MD = 'AGENTS.md';
export const CHAT_RULES_MD = 'chat-rules.md';

export const DUO_PANEL_EMPTY_STATE_EVENTS = {
  VIEW_TRIAL_EXPIRED: 'view_duo_agentic_trial_expired_empty_state',
  VIEW_NOT_AVAILABLE: 'view_duo_agentic_not_available_empty_state',
  CLICK_TRIAL_EXPIRED_UPGRADE: 'click_duo_agentic_trial_expired_upgrade',
  CLICK_TRIAL_EXPIRED_LEARN_MORE: 'click_duo_agentic_trial_expired_learn_more',
  CLICK_START_TRIAL: 'click_duo_agentic_not_available_empty_state_start_trial',
  CLICK_LEARN_MORE: 'click_duo_agentic_not_available_empty_state_learn_more',
  CLICK_BUY_CREDITS: 'click_duo_agentic_not_available_empty_state_buy_credits',
  CLICK_LEARN_MORE_CREDITS: 'click_duo_agentic_not_available_empty_state_learn_more_credits',
  VIEW_IDENTITY_VERIFICATION: 'view_duo_agentic_identity_verification_empty_state',
  CLICK_VERIFY_ACCOUNT: 'click_duo_agentic_identity_verification_empty_state_verify_account',
};

export const NEW_AGENTIC_CHAT_FLOW_CONFIG = {
  flowConfigId: 'agentic_chat',
  flowVersion: '1.0.0',
  flowConfigSchemaVersion: 'v1',
};
